import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Tabs,
  Descriptions,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Spin,
  message,
  Statistic,
  Row,
  Col,
  Card,
} from 'antd';
import {
  TeamOutlined,
  DatabaseOutlined,
  SettingOutlined,
  BarChartOutlined,
  UserOutlined,
  SafetyOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { Company } from '../../types/company';
import { CompanyInstance } from '../../types/companyInstance';
import { CompanyService } from '../../services/api/companyService';
import { CompanyInstanceService } from '../../services/api/companyInstanceService';
import { UserService, User } from '../../services/api/userService';
import { UserCompanyAssignment } from './UserCompanyAssignment';
import { CompanyPermissionManager } from './CompanyPermissionManager';
import { CompanySubscriptionTab } from './CompanySubscriptionTab';
import styles from './CompanyDetail.module.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface CompanyDetailProps {
  company: Company | null;
  visible: boolean;
  onClose: () => void;
}

/**
 * Company detail component for viewing and managing company/business entity details
 * @feature CORE_BUSINESS_ENTITY - Enables business entity management and viewing
 */
export const CompanyDetail: React.FC<CompanyDetailProps> = ({ company, visible, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState<Array<{instance: CompanyInstance, user: User}>>([]);
  const [instances, setInstances] = useState<CompanyInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<any>({});

  useEffect(() => {
    if (company && visible) {
      loadCompanyData();
    }
  }, [company, visible]);

  const loadCompanyData = async () => {
    if (!company) return;
    
    setLoading(true);
    try {
      // Load company instances
      const companyInstances = await CompanyInstanceService.getAll({ company_id: company.id });
      setInstances(companyInstances);

      // Fetch user details for each instance
      const instancesWithUsers = await Promise.all(
        companyInstances.map(async (instance) => {
          try {
            const user = await UserService.getById(instance.user_id);
            return { instance, user: user || null };
          } catch {
            return { instance, user: null };
          }
        })
      );

      // Filter out instances where user couldn't be fetched and set users
      const userData = instancesWithUsers.filter(item => item.user !== null) as Array<{instance: CompanyInstance, user: any}>;
      setUsers(userData);

      // Load analytics
      const companyAnalytics = await CompanyService.getAnalytics(company.id);
      setAnalytics(companyAnalytics);
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserAssignmentChange = async () => {
    // Reload company instances and users after assignment changes
    if (company) {
      const companyInstances = await CompanyInstanceService.getAll({ company_id: company.id });
      setInstances(companyInstances);

      // Fetch user details for each instance
      const instancesWithUsers = await Promise.all(
        companyInstances.map(async (instance) => {
          try {
            const user = await UserService.getById(instance.user_id);
            return { instance, user: user || null };
          } catch {
            return { instance, user: null };
          }
        })
      );

      // Filter out instances where user couldn't be fetched
      const userData = instancesWithUsers.filter(item => item.user !== null) as Array<{instance: CompanyInstance, user: any}>;
      setUsers(userData);
    }
  };

  const userColumns = [
    {
      title: 'User',
      key: 'user',
      render: (record: {instance: CompanyInstance, user: any}) => (
        <Space>
          <UserOutlined />
          <Text>{record.user.username || record.user.email || record.instance.user_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Full Name',
      key: 'fullName',
      render: (record: {instance: CompanyInstance, user: User}) => (
        <Text>
          {record.user.full_name || <Text type="secondary">Not set</Text>}
        </Text>
      ),
    },
    {
      title: 'Instance Name',
      key: 'instance_name',
      render: (record: {instance: CompanyInstance, user: any}) => (
        <Text>{record.instance.instance_name || <Text type="secondary">Not set</Text>}</Text>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      render: (record: {instance: CompanyInstance, user: any}) => 
        record.instance.role ? <Tag>{record.instance.role}</Tag> : <Text type="secondary">No role</Text>,
    },
    {
      title: 'Status',
      key: 'is_active',
      render: (record: {instance: CompanyInstance, user: any}) => (
        <Tag color={record.instance.is_active ? 'green' : 'red'}>
          {record.instance.is_active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Joined',
      key: 'joined_at',
      render: (record: {instance: CompanyInstance, user: any}) => 
        record.instance.joined_at ? new Date(record.instance.joined_at).toLocaleDateString() : <Text type="secondary">Not set</Text>
    },
  ];

  const instanceColumns = [
    {
      title: 'Instance Name',
      dataIndex: 'instance_name',
      key: 'instance_name',
    },
    {
      title: 'User',
      key: 'user',
      render: (record: any) => (
        <Text>{record.user_id}</Text>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => role ? <Tag>{role}</Tag> : <Text type="secondary">No role</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  if (!company) return null;

  return (
    <Drawer
      title={<Title level={4}>{company.name}</Title>}
      placement="right"
      width={800}
      onClose={onClose}
      open={visible}
      className={styles.companyDetailDrawer}
    >
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <span>
                <DatabaseOutlined />
                Overview
              </span>
            }
            key="overview"
          >
            <Descriptions bordered column={1} style={{ marginBottom: '24px' }}>
              <Descriptions.Item label="Name">{company.name}</Descriptions.Item>
              <Descriptions.Item label="Description">
                {company.description || <Text type="secondary">No description</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Industry">
                {company.industry || <Text type="secondary">Not set</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Size">
                {company.size || <Text type="secondary">Not set</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Website">
                {company.website ? (
                  <a href={company.website} target="_blank" rel="noopener noreferrer">
                    {company.website}
                  </a>
                ) : (
                  <Text type="secondary">Not set</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {company.email || <Text type="secondary">Not set</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {company.phone || <Text type="secondary">Not set</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {company.address || <Text type="secondary">Not set</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={company.is_active ? 'green' : 'red'}>
                  {company.is_active ? 'Active' : 'Inactive'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(company.created_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {new Date(company.updated_at).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            <Row gutter={16}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Users"
                    value={users.length}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Instances"
                    value={instances.length}
                    prefix={<DatabaseOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Active Instances"
                    value={instances.filter((i: any) => i.is_active).length}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span>
                <TeamOutlined />
                Users ({users.length})
              </span>
            }
            key="users"
          >
            <UserCompanyAssignment
              companyId={company.id}
              onAssignmentChange={handleUserAssignmentChange}
            />
            <div style={{ marginTop: '24px' }}>
              <Title level={5}>Assigned Users</Title>
              <Table
                columns={userColumns}
                dataSource={users}
                rowKey={(record) => record.instance.id}
                pagination={{ pageSize: 10 }}
              />
            </div>
          </TabPane>

          <TabPane
            tab={
              <span>
                <CreditCardOutlined />
                Subscriptions
              </span>
            }
            key="subscriptions"
          >
            <CompanySubscriptionTab companyId={company.id} />
          </TabPane>

          <TabPane
            tab={
              <span>
                <DatabaseOutlined />
                Instances ({instances.length})
              </span>
            }
            key="instances"
          >
            <Table
              columns={instanceColumns}
              dataSource={instances}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <SafetyOutlined />
                Permissions
              </span>
            }
            key="permissions"
          >
            <CompanyPermissionManager companyId={company.id} />
          </TabPane>

          <TabPane
            tab={
              <span>
                <SettingOutlined />
                Settings
              </span>
            }
            key="settings"
          >
            <Card>
              <Title level={5}>Company Settings</Title>
              <Text type="secondary">Settings management coming soon</Text>
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <BarChartOutlined />
                Analytics
              </span>
            }
            key="analytics"
          >
            <Card>
              <Title level={5}>Company Analytics</Title>
              {Object.keys(analytics).length > 0 ? (
                <pre>{JSON.stringify(analytics, null, 2)}</pre>
              ) : (
                <Text type="secondary">No analytics data available</Text>
              )}
            </Card>
          </TabPane>
        </Tabs>
      </Spin>
    </Drawer>
  );
};
